import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-test-id"]
const _hoisted_2 = { class: "grid gap-3 mt-6 racq-max-w-xl5" }
const _hoisted_3 = { class: "text-m md:mt-4 mt-2" }
const _hoisted_4 = ["onClick"]

import { useAccountStore, usePageStore } from '@/store';
import { useSelfServiceInsuranceClaims } from '@/store/self-service/insurance-claims.state';
import { computed, ref, watch, onMounted } from 'vue';
import PanelTitle from '../PanelTitle/PanelTitle.vue';
import { ComponentRendering, Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import Notification from '../Notification/Notification.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { getInsuranceTypeDisplayName, IIncompleteClaim } from '@/interfaces';
import { useI18n } from 'vue-i18n';
import { getCoverageTitle } from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { dateToDateString } from '@/utils/data-transforms';
import { useRoute } from 'vue-router';
import { toProperCase } from '@/utils/text-transforms';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClaimsPanel',
  props: {
    fields: {},
    rendering: {}
  },
  emits: ['hasIncompleteClaims'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const route = useRoute();

const outerClass = props.rendering?.params?.Styles;
const inlineStyle = props.rendering?.params?.inlineStyle;
const dataTestId = usePageStore().dataTestId(props.rendering);
const accountStore = useAccountStore();
const insuranceClaimsStore = useSelfServiceInsuranceClaims();
const incompleteClaims = ref<IIncompleteClaim[]>([]);

const emit = __emit;

onMounted(() => {
  fetchClaims();
});

watch(
  () => accountStore.identityDetails?.portfolio?.insurancePolicies,
  () => {
    fetchClaims();
  }
);

const fetchClaims = () => {
  const policyNumbers = accountStore.identityDetails?.portfolio?.insurancePolicies
    ?.map((policy) => policy.policyNumber) // Map policy number
    .filter((policyNumber) => !route?.query?.policyNumber || route?.query?.policyNumber == policyNumber); // Filter for product details view
  policyNumbers?.length &&
    insuranceClaimsStore.getIncompleteClaims(policyNumbers).then((res) => (incompleteClaims.value = res));
};

const displayClaims = computed(() => {
  return incompleteClaims.value
    .map((claim) => {
      // get policy details
      const policy = accountStore.identityDetails?.portfolio?.insurancePolicies?.find(
        (policy) => policy.policyNumber == claim.policyNumber
      );
      if (!policy) return;
      // map display lines

      //try and find coverage for motor
      let coverage = policy?.coverages.find(
        (x: any) => x.vehicle?.registrationNumber?.toLowerCase() == claim.claimItem?.toLowerCase()
      );

      //try and find coverage for home
      if (!coverage) {
        coverage = policy?.coverages.find(
          (x: any) =>
            x.riskAddress?.formattedLong?.toLowerCase()?.replaceAll(',', '') ==
            claim.claimItem?.toLowerCase()?.replaceAll(',', '')
        );
      }

      //try and find coverage for pet
      if (!coverage) {
        coverage = policy?.coverages.find(
          (x: any) =>
            x.petName?.toLowerCase()?.replaceAll(',', '') == claim.claimItem?.toLowerCase()?.replaceAll(',', '')
        );
      }

      const coverType = getInsuranceTypeDisplayName(dictionary, coverage?.coverageCode ?? '', false);

      let claimDetails: string | undefined;
      // Pet claims could have no incident date
      if (claim.incidentDate) {
        //if a dash is detected it indicates that claims details should be formatted like this with a dash included after the loss type: 'Fire - occurred on 15/08/2025'.
        const appendDash =
          props.fields?.claimDetails?.value?.trim()?.startsWith('-') && claim.lossCategory.length > 0 ? '-' : '';

        claimDetails = props.fields?.claimDetails?.value
          ?.replace('{{lossCategory}}', `${claim.lossCategory} ${appendDash} `)
          .replace('{{incidentDate}}', dateToDateString(claim.incidentDate, 'dd/MM/yyyy'));

        if (appendDash.length > 0 && claimDetails) {
          const splitDetails = claimDetails.split('-');
          claimDetails = `${splitDetails[1]} - ${splitDetails[2]}`;
        } else {
          claimDetails = claimDetails?.replace('-', '').trim();
          if (claimDetails) {
            claimDetails = `${claimDetails.charAt(0).toUpperCase()}${claimDetails?.slice(1)}`;
          }
        }
      }

      return {
        title: props.fields?.brief?.value?.replace('{{coverType}}', coverType),
        coverageTitle: getCoverageTitle(policy, claim.claimItem),
        claimRef: props.fields?.claimRef?.value?.replace('{{claimNumber}}', claim.referenceNumber),
        claimDetails,
        expiryDetails: props.fields?.expiryDetails?.value
          ?.replace('{{savedAt}}', dateToDateString(claim.savedAt, 'dd/MM/yyyy'))
          .replace('{{daysExpiry}}', claim.daysExpiry?.toString()),
        saveUrl: claim.saveUrl,
      };
    })
    .filter((o) => o);
});

watch(
  () => displayClaims?.value?.length,
  () => {
    emit('hasIncompleteClaims', displayClaims?.value?.length > 0);
  }
);

const resumeClaim = (url: string | undefined) => {
  url && (window.location.href = url);
};

return (_ctx: any,_cache: any) => {
  return (displayClaims.value.length)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        "data-test-id": _unref(dataTestId),
        class: _normalizeClass(_unref(outerClass)),
        style: _normalizeStyle(_unref(inlineStyle))
      }, [
        _createVNode(PanelTitle, {
          title: props.fields?.title!,
          subTitle: props.fields?.subtitle!
        }, null, 8, ["title", "subTitle"]),
        _createElementVNode("div", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayClaims.value, (claim, index) => {
            return (_openBlock(), _createBlock(Notification, {
              key: index,
              type: _unref(InformationBoxTypes).INFO,
              title: claim?.title
            }, {
              buttons: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_unref(RichText), {
                    class: "text-m-bold",
                    field: { value: _unref(toProperCase)(claim?.coverageTitle) }
                  }, null, 8, ["field"]),
                  _createVNode(_unref(RichText), {
                    class: "text-m-bold",
                    field: { value: claim?.claimRef }
                  }, null, 8, ["field"]),
                  (claim?.claimDetails)
                    ? (_openBlock(), _createBlock(_unref(RichText), {
                        key: 0,
                        field: { value: claim?.claimDetails }
                      }, null, 8, ["field"]))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(RichText), {
                    field: { value: claim?.expiryDetails }
                  }, null, 8, ["field"]),
                  _createElementVNode("button", {
                    class: "mt-4 btn btn-sm btn-primary",
                    onClick: () => resumeClaim(claim?.saveUrl)
                  }, _toDisplayString(props.fields?.resumeBtn?.value), 9, _hoisted_4)
                ])
              ]),
              _: 2
            }, 1032, ["type", "title"]))
          }), 128))
        ])
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})